import type {
  ClubBillingDto,
  ClubBillingInput,
  ClubUserDto,
  InviteBackofficeUserInput,
  InviteClubMemberInput,
  RoleDto,
  UpdateUserInput,
  PasskeyDto,
  PreferencesDto,
  ProfileDto,
  ProfilePhotoDto,
  SecurityState,
  UpdatePreferencesInput,
  UpdateProfileInput,
  UploadTargetDto,
  UserAccountStatus,
} from "@tc/shared";
import { authClient, getSession } from "@/lib/auth/client";

/**
 * Account-security data access (TW-3455). Two-factor enable/verify/disable and passkey
 * *registration* run through the Better Auth client plugins (they own the WebAuthn ceremony and
 * the password challenge). Listing and deleting passkeys, and reading the current factor state,
 * are plain reads the client plugin doesn't surface as actions, so we hit the endpoints directly:
 *   • GET  /api/auth/passkey/list-user-passkeys — the caller's registered credentials
 *   • POST /api/auth/passkey/delete-passkey      — remove one by id
 *   • GET  /api/me                               — twoFactorEnabled + passkeyCount
 * All same-origin (the Vite proxy forwards /api), so the session cookie rides along.
 */
const API_URL = import.meta.env.VITE_API_URL ?? "/api";
const AUTH_URL = `${API_URL}/auth`;

/** Better Auth's passkey row carries more than we render; keep just the display fields. */
interface RawPasskey {
  id: string;
  name?: string | null;
  createdAt?: string | null;
}

export async function listPasskeys(): Promise<PasskeyDto[]> {
  const res = await fetch(`${AUTH_URL}/passkey/list-user-passkeys`, {
    headers: { accept: "application/json" },
  });
  if (!res.ok) return [];
  const rows = (await res.json()) as RawPasskey[];
  return rows.map((p) => ({
    id: p.id,
    name: p.name ?? null,
    createdAt: p.createdAt ?? new Date().toISOString(),
  }));
}

export async function deletePasskey(id: string): Promise<boolean> {
  const res = await fetch(`${AUTH_URL}/passkey/delete-passkey`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ id }),
  });
  return res.ok;
}

/** Read the current second-factor state from /api/me (twoFactorEnabled + passkeyCount). */
export async function fetchSecurityState(): Promise<SecurityState> {
  const res = await fetch(`${API_URL}/me`, { headers: { accept: "application/json" } });
  if (!res.ok) return { twoFactorEnabled: false, passkeyCount: 0 };
  const me = (await res.json()) as { twoFactorEnabled?: boolean; passkeyCount?: number };
  return {
    twoFactorEnabled: Boolean(me.twoFactorEnabled),
    passkeyCount: me.passkeyCount ?? 0,
  };
}

/* ── Profile (TW-3464) ─────────────────────────────────────────────────────────────────────
 * Self-service edits to the caller's own identity row. The avatar is the TW-3447 three-step
 * signed-URL dance: sign an upload URL, PUT the bytes straight to it, then attach the key — so
 * the image bytes never pass through this API's JSON layer and only the private key is persisted.
 */

/** Status code carried on a failed profile save so the form can surface the right inline error. */
export class ProfileSaveError extends Error {
  constructor(readonly status: number) {
    super(`Profile save failed (${status})`);
  }
}

/** PATCH /api/me/profile — persist name/email/jobTitle/phone; throws ProfileSaveError on 4xx. */
export async function updateProfile(input: UpdateProfileInput): Promise<ProfileDto> {
  const res = await fetch(`${API_URL}/me/profile`, {
    method: "PATCH",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ProfileSaveError(res.status);
  return (await res.json()) as ProfileDto;
}

/** Upload a new avatar (sign → PUT bytes → attach) and return the freshly-signed image URL. */
export async function uploadProfilePhoto(file: File): Promise<ProfilePhotoDto> {
  const signRes = await fetch(`${API_URL}/me/photo/upload-url`, {
    method: "POST",
    headers: { accept: "application/json" },
  });
  if (!signRes.ok) throw new ProfileSaveError(signRes.status);
  const target = (await signRes.json()) as UploadTargetDto;

  const putRes = await fetch(target.url, {
    method: "PUT",
    headers: { "content-type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!putRes.ok) throw new ProfileSaveError(putRes.status);

  const attachRes = await fetch(`${API_URL}/me/photo`, {
    method: "PUT",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ key: target.key }),
  });
  if (!attachRes.ok) throw new ProfileSaveError(attachRes.status);
  return (await attachRes.json()) as ProfilePhotoDto;
}

/** DELETE /api/me/photo — remove the avatar (falls back to initials). */
export async function removeProfilePhoto(): Promise<ProfilePhotoDto> {
  const res = await fetch(`${API_URL}/me/photo`, {
    method: "DELETE",
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new ProfileSaveError(res.status);
  return (await res.json()) as ProfilePhotoDto;
}

/* ── Billing details (TW-3527) ─────────────────────────────────────────────────────────────
 * The club's own invoicing identity, edited in Settings → Billing. Self-scoped: the club comes
 * from the session, so these calls carry no club id (the back-office equivalent addresses a club by
 * id — see features/clubs). Saving appends a new billing version server-side; the accounting email
 * is updated in place. Reuses ProfileSaveError to carry the status for the form's error handling.
 */

/** GET /api/billing — the caller's own club's current billing details. */
export async function fetchBilling(): Promise<ClubBillingDto> {
  const res = await fetch(`${API_URL}/billing`, { headers: { accept: "application/json" } });
  if (!res.ok) throw new ProfileSaveError(res.status);
  return (await res.json()) as ClubBillingDto;
}

/** PUT /api/billing — save the caller's own club's billing details (appends a new version). */
export async function updateBilling(input: ClubBillingInput): Promise<ClubBillingDto> {
  const res = await fetch(`${API_URL}/billing`, {
    method: "PUT",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ProfileSaveError(res.status);
  return (await res.json()) as ClubBillingDto;
}

/* ── Notification preferences (TW-3458) ────────────────────────────────────────────────────
 * The two global channel toggles (email / in-app), honoured at the source by the notification
 * fan-out (TW-3435). v1 has no per-event granularity, so the Settings matrix binds every category
 * row's two switches to these same two values.
 */

/** The global notification channel toggles (mirrors the API's NotificationPrefs). */
export interface NotificationPrefs {
  email: boolean;
  inApp: boolean;
}

/** GET /api/me/notification-prefs — the caller's resolved (defaults-applied) channel toggles. */
export async function fetchNotificationPrefs(): Promise<NotificationPrefs> {
  const res = await fetch(`${API_URL}/me/notification-prefs`, { headers: { accept: "application/json" } });
  if (!res.ok) return { email: true, inApp: true };
  return (await res.json()) as NotificationPrefs;
}

/** PATCH /api/me/notification-prefs — update one or both channels; returns the resolved prefs. */
export async function updateNotificationPrefs(patch: Partial<NotificationPrefs>): Promise<NotificationPrefs> {
  const res = await fetch(`${API_URL}/me/notification-prefs`, {
    method: "PATCH",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new ProfileSaveError(res.status);
  return (await res.json()) as NotificationPrefs;
}

/* ── Interface preferences (TW-3466) ───────────────────────────────────────────────────────
 * Timezone + density, written together via PATCH /api/me/preferences (partial). Language has its
 * own endpoint (PATCH /api/me/language) driven by LanguageProvider.
 */

/** PATCH /api/me/preferences — update timezone and/or density; returns the resolved preferences. */
export async function updatePreferences(patch: UpdatePreferencesInput): Promise<PreferencesDto> {
  const res = await fetch(`${API_URL}/me/preferences`, {
    method: "PATCH",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new ProfileSaveError(res.status);
  return (await res.json()) as PreferencesDto;
}

/* ── Password change & active sessions (TW-3465) ───────────────────────────────────────────
 * These run through Better Auth's *core* client methods (not the 2FA/passkey plugins). The server
 * owns the security-sensitive parts: change-password verifies the current password and re-hashes
 * the new one; revoke deletes the opaque session row. We never revoke the current session here —
 * a successful password change must keep the user signed in, and a device only signs *itself* out
 * via the topbar, so the card never offers a self-revoke (the current row carries no action).
 */

/** Distinguishes a wrong current password from any other change-password failure for the form. */
export class PasswordChangeError extends Error {
  constructor(readonly reason: "wrongCurrent" | "weak" | "failed") {
    super(`Password change failed (${reason})`);
  }
}

/**
 * Change the signed-in user's password. `revokeOtherSessions: false` is deliberate: the acceptance
 * requires the current session (and every other device) to stay valid. Better Auth re-checks the
 * current password and returns INVALID_PASSWORD on a mismatch, which we map for the inline error.
 */
export async function changeAccountPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const res = await authClient.changePassword({
    currentPassword: input.currentPassword,
    newPassword: input.newPassword,
    revokeOtherSessions: false,
  });
  if (res.error) {
    const code = res.error.code ?? "";
    if (code === "INVALID_PASSWORD") throw new PasswordChangeError("wrongCurrent");
    if (code === "PASSWORD_TOO_SHORT" || code === "PASSWORD_TOO_LONG") throw new PasswordChangeError("weak");
    throw new PasswordChangeError("failed");
  }
}

/** An active session as rendered in the sessions card — a thin view of Better Auth's session row. */
export interface ActiveSession {
  /** The session token — what `revokeSession` keys off (NOT the row id). */
  token: string;
  /** Friendly "Browser · OS" label, or null when the user-agent can't be parsed. */
  device: string | null;
  /** Source IP (no geo-IP lookup — real location is out of scope), or null. */
  ipAddress: string | null;
  /** When the session was last touched (Better Auth `updatedAt`) — rendered relative. */
  lastActive: string;
  /** True for the device making this request; it can't revoke itself from the list. */
  current: boolean;
  /** True once past its 8h expiry — shown as history (no revoke action), not a live session. */
  expired: boolean;
}

/** How far back the sessions card shows sign-in activity (expired rows included as history). */
const ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * List the caller's sign-in activity over the last 7 days, flagging the current device. Better Auth
 * has no "is this me" marker, so we match each row's token against the live session token from
 * getSession() — both come from the same session table, so the tokens are identical.
 *
 * Better Auth keeps a session row per sign-in (absolute 8h expiry, no refresh — see auth.ts) and
 * never prunes them, so this shows recent activity, not just live sessions: rows within the 7-day
 * window are kept and an `expired` flag marks those past their 8h validity (history, not revocable).
 * The current device sorts first, then by most-recent activity (so live sessions lead, expired
 * trail). Older-than-7-days rows are dropped.
 */
export async function listActiveSessions(): Promise<ActiveSession[]> {
  const [list, current] = await Promise.all([authClient.listSessions(), getSession()]);
  const now = Date.now();
  const currentToken = current.data?.session.token ?? null;
  return (list.data ?? [])
    .map((s) => ({
      token: s.token,
      device: deviceLabel(s.userAgent ?? null),
      ipAddress: s.ipAddress || null,
      lastActive: (s.updatedAt ?? s.createdAt).toISOString(),
      current: s.token === currentToken,
      expired: new Date(s.expiresAt).getTime() <= now,
    }))
    .filter((s) => now - new Date(s.lastActive).getTime() <= ACTIVITY_WINDOW_MS)
    .sort((a, b) =>
      a.current === b.current ? b.lastActive.localeCompare(a.lastActive) : a.current ? -1 : 1,
    );
}

/** Revoke a single session by token (Better Auth keys off the token, not the row id). */
export async function revokeSessionByToken(token: string): Promise<boolean> {
  const res = await authClient.revokeSession({ token });
  return !res.error;
}

/** Revoke every session except the current one (the "sign out everywhere else" action). */
export async function revokeOtherSessions(): Promise<boolean> {
  const res = await authClient.revokeOtherSessions();
  return !res.error;
}

/* ── Team (club self-service) ───────────────────────────────────────────────────────────────
 * A club user managing the members of their own club, mirroring the back-office user admin but
 * self-scoped: the club comes from the session, so none of these calls carry a club id. Roles are
 * hidden in the club portal, so inviting takes only name + email and "remove" is a status change.
 */

/** A failed team call, carrying the server's message + status so the UI can surface it inline. */
export class TeamError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "TeamError";
  }
}

async function teamReq<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { accept: "application/json", "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message) message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new TeamError(message, res.status);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/** GET /api/team — the caller's own club's users. */
export function listTeam(): Promise<ClubUserDto[]> {
  return teamReq<ClubUserDto[]>("/team");
}

/** POST /api/team — invite a new member to the caller's own club (emails a set-password link). */
export function inviteTeamMember(body: InviteClubMemberInput): Promise<ClubUserDto> {
  return teamReq<ClubUserDto>("/team", { method: "POST", body: JSON.stringify(body) });
}

/** PATCH /api/team/:id — disable ("remove") or re-enable ("restore") a member; revokes on disable. */
export function setTeamMemberStatus(id: string, status: UserAccountStatus): Promise<ClubUserDto> {
  return teamReq<ClubUserDto>(`/team/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
}

/* ── Team (back office) ─────────────────────────────────────────────────────────────────────
 * A TC admin managing their own back-office colleagues, mirroring the club self-service team but
 * org-scoped and role-aware: back-office roles (Admin / Staff / Read-Only) differ meaningfully, so
 * inviting carries a role and a teammate's role can be changed. Same gate as the club variant
 * (`user.manage`) — only Back Office Admins hold it.
 */

/** GET /api/backoffice/roles — the assignable back-office roles (for the pickers). */
export function listBackofficeRoles(): Promise<RoleDto[]> {
  return teamReq<RoleDto[]>("/backoffice/roles");
}

/** GET /api/backoffice/users — every back-office user. */
export function listBackofficeTeam(): Promise<ClubUserDto[]> {
  return teamReq<ClubUserDto[]>("/backoffice/users");
}

/** POST /api/backoffice/users — invite a TC teammate (emails a set-password link). */
export function inviteBackofficeUser(body: InviteBackofficeUserInput): Promise<ClubUserDto> {
  return teamReq<ClubUserDto>("/backoffice/users", { method: "POST", body: JSON.stringify(body) });
}

/** PATCH /api/backoffice/users/:id — change a teammate's status and/or role. */
export function updateBackofficeUser(id: string, body: UpdateUserInput): Promise<ClubUserDto> {
  return teamReq<ClubUserDto>(`/backoffice/users/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

/** POST /api/backoffice/users/:id/resend — re-send a still-pending teammate's invite email. */
export function resendBackofficeInvite(id: string): Promise<ClubUserDto> {
  return teamReq<ClubUserDto>(`/backoffice/users/${id}/resend`, { method: "POST" });
}

/**
 * A friendly "Browser · OS" label from a User-Agent string, or null when nothing recognisable is
 * present (the card shows a localised "Unknown device" fallback then). Deliberately tiny — real
 * device fingerprinting is out of scope; this just makes each session row recognisable.
 */
export function deviceLabel(userAgent: string | null): string | null {
  if (!userAgent) return null;
  const browser = /Edg\//.test(userAgent)
    ? "Edge"
    : /OPR\/|Opera/.test(userAgent)
      ? "Opera"
      : /Firefox\//.test(userAgent)
        ? "Firefox"
        : /Chrome\//.test(userAgent)
          ? "Chrome"
          : /Safari\//.test(userAgent)
            ? "Safari"
            : null;
  const os = /iPhone|iPad|iPod/.test(userAgent)
    ? "iOS"
    : /Android/.test(userAgent)
      ? "Android"
      : /Mac OS X|Macintosh/.test(userAgent)
        ? "macOS"
        : /Windows/.test(userAgent)
          ? "Windows"
          : /Linux/.test(userAgent)
            ? "Linux"
            : null;
  const parts = [browser, os].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}
